/**
 * SwipeableTaskCard Component
 * FinTasks 2.0 - Phase 3
 * 
 * Wrapper component that adds swipe gestures to TaskCard for mobile users.
 * Swipe right to complete, swipe left to delete.
 * 
 * SAFETY: This is a wrapper that preserves existing TaskCard functionality.
 * 
 * FIX (2024-12-16): Added explicit snap-back animation to prevent jitter.
 * The issue was that dragElastic + constraints were fighting, causing oscillation.
 * Now we use animate() to smoothly return to x=0 when below threshold.
 * 
 * @version 2.0.1 - Jitter fix
 * @date 2024-12-16
 */

import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { CheckCircle2, Trash2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import TaskCard from './TaskCard';
import { TASK_STATUS } from '@/services/finTasksService';

// Swipe threshold in pixels - must exceed this to trigger action
// Below this threshold, the card snaps back to original position
const SWIPE_THRESHOLD = 80;
const SWIPE_VELOCITY_THRESHOLD = 500;

export default function SwipeableTaskCard({
  task,
  onToggle,
  onEdit,
  onDelete,
  onArchive,
  onUnarchive,
  onClick,
  className,
  enableSwipe = true // Can be disabled for desktop or by user preference
}) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null
  const constraintsRef = useRef(null);

  const x = useMotionValue(0);
  
  // Background colors based on swipe direction
  const backgroundRight = useTransform(
    x,
    [0, SWIPE_THRESHOLD],
    ['rgba(22, 163, 74, 0)', 'rgba(22, 163, 74, 1)'] // Green for complete
  );
  
  const backgroundLeft = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0],
    ['rgba(220, 38, 38, 1)', 'rgba(220, 38, 38, 0)'] // Red for delete
  );

  // Icon scale based on swipe distance
  const rightIconScale = useTransform(x, [0, SWIPE_THRESHOLD], [0.5, 1]);
  const leftIconScale = useTransform(x, [-SWIPE_THRESHOLD, 0], [1, 0.5]);

  const isDone = task.status === TASK_STATUS.DONE;

  /**
   * Handle drag end - determine if action should trigger or snap back
   * FIX: Use animate() for smooth snap-back to prevent jitter/oscillation
   */
  const handleDragEnd = (event, info) => {
    const { offset, velocity } = info;
    const currentX = x.get();

    // Swipe right to complete/uncomplete
    // Trigger if past threshold OR high velocity in that direction
    if (offset.x > SWIPE_THRESHOLD || (velocity.x > SWIPE_VELOCITY_THRESHOLD && currentX > 20)) {
      setSwipeDirection('right');
      // Trigger complete action
      if (onToggle) {
        onToggle(task);
      }
      // Animate back to 0 after action completes
      setTimeout(() => {
        setSwipeDirection(null);
        animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
      }, 300);
      return;
    }

    // Swipe left to delete
    // Trigger if past threshold OR high velocity in that direction
    if (offset.x < -SWIPE_THRESHOLD || (velocity.x < -SWIPE_VELOCITY_THRESHOLD && currentX < -20)) {
      setSwipeDirection('left');
      // Trigger delete action
      if (onDelete) {
        onDelete(task);
      }
      // Note: Card may be removed from DOM by delete, so no need to animate back
      return;
    }

    // FIX: Explicitly animate back to x=0 when below threshold
    // This prevents the jitter caused by dragElastic + constraints fighting
    setSwipeDirection(null);
    animate(x, 0, { 
      type: 'spring', 
      stiffness: 500,  // High stiffness for snappy return
      damping: 30      // Sufficient damping to prevent oscillation
    });
  };

  // If swipe is disabled, just render the regular TaskCard
  if (!enableSwipe) {
    return (
      <TaskCard
        task={task}
        onToggle={onToggle}
        onEdit={onEdit}
        onDelete={onDelete}
        onArchive={onArchive}
        onUnarchive={onUnarchive}
        onClick={onClick}
        className={className}
      />
    );
  }

  return (
    <div ref={constraintsRef} className="relative overflow-hidden rounded-xl">
      {/* Background actions revealed on swipe */}
      <div className="absolute inset-0 flex">
        {/* Right swipe background (Complete) */}
        <motion.div
          style={{ backgroundColor: backgroundRight }}
          className="flex-1 flex items-center justify-start pl-6"
        >
          <motion.div style={{ scale: rightIconScale }}>
            {isDone ? (
              <RotateCcw className="text-white" size={28} />
            ) : (
              <CheckCircle2 className="text-white" size={28} />
            )}
          </motion.div>
          <motion.span 
            style={{ scale: rightIconScale }}
            className="ml-2 text-white font-semibold text-sm"
          >
            {isDone ? 'Reopen' : 'Complete'}
          </motion.span>
        </motion.div>

        {/* Left swipe background (Delete) */}
        <motion.div
          style={{ backgroundColor: backgroundLeft }}
          className="flex-1 flex items-center justify-end pr-6"
        >
          <motion.span 
            style={{ scale: leftIconScale }}
            className="mr-2 text-white font-semibold text-sm"
          >
            Delete
          </motion.span>
          <motion.div style={{ scale: leftIconScale }}>
            <Trash2 className="text-white" size={28} />
          </motion.div>
        </motion.div>
      </div>

      {/* Draggable TaskCard */}
      <motion.div
        style={{ x }}
        drag="x"
        dragConstraints={{ left: -150, right: 150 }}
        dragElastic={0.05}  // FIX: Reduced elastic to prevent bounce-back fighting
        dragMomentum={false} // FIX: Disable momentum to prevent overshoot oscillation
        onDragEnd={handleDragEnd}
        whileTap={{ cursor: 'grabbing' }}
        className="relative z-10 touch-pan-y"
      >
        <TaskCard
          task={task}
          onToggle={onToggle}
          onEdit={onEdit}
          onDelete={onDelete}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onClick={onClick}
          className={cn(className, 'shadow-sm')}
        />
      </motion.div>
    </div>
  );
}

/**
 * Hook to detect if device supports touch
 */
export function useIsTouchDevice() {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}
